import { OfferedValue, OfferedValues } from "components/Document/ReviewInteraction";
import { get } from "lodash-es";
import { ReactNode, memo } from "react";
import { Control, useWatch } from "react-hook-form";

import type { ManifestFormValues } from "../types";

/**
 * One field's offered values, subscribed to that field alone.
 *
 * Every field with something on offer renders one of these, so about sixty are
 * mounted at once. Each needs the value its own input currently holds — to drop
 * an offer that matches it, and to name what keeping would keep — and reading
 * all sixty from one subscription in the dialog meant every keystroke
 * re-rendered the entire form and the PDF pane beside it.
 *
 * `compute` rather than a watch on the path: the accessory is addressed by a
 * plain string, and react-hook-form only narrows a subscription for a statically
 * known field path. Computing from the whole form gets the same result — the
 * comparison is on the computed text, so a keystroke in any *other* field
 * produces an unchanged string here and no render at all.
 *
 * Memoized because the field above it is not. A row of chips is several MUI
 * components, sixty of them are mounted, and they re-rendered with their field
 * for every reason the dialog re-rendered — none of which change what a source
 * holds. The props must stay stable for this to hold: `candidates` comes from a
 * per-path cache for exactly that reason.
 */
export const ManifestFieldOffers = memo(function ManifestFieldOffers({
  control,
  name,
  label,
  candidates,
  canKeep,
  onTake,
  onKeep,
}: {
  control: Control<ManifestFormValues>;
  name: string;
  label?: ReactNode;
  /** Everything another source holds for this field, before the field's own value is excluded. */
  candidates: readonly OfferedValue<string>[];
  /** Whether something is asking about this field, so keeping it is an answer. */
  canKeep: boolean;
  onTake: (path: string, offer: OfferedValue<string>) => void;
  onKeep: (path: string) => void;
}) {
  const currentText = useWatch({
    control,
    compute: (values) => {
      const value = get(values, name);
      return value == null ? "" : String(value);
    },
  });

  // A source agreeing with the form is not an offer: a row reading "e-Manifest
  // 141" beside "Keep 141" asks the reviewer to compare two identical values.
  // Filtering on the live value rather than a load-time flag is also what keeps
  // taking one reversible — the value it replaced is immediately on offer again.
  const offers = candidates.filter((offer) => String(offer.value) !== currentText);

  if (offers.length === 0 && !canKeep) return null;

  return (
    <OfferedValues
      offers={offers}
      onTake={(offer) => onTake(name, offer)}
      onKeep={canKeep ? () => onKeep(name) : undefined}
      keepValue={currentText}
      ariaLabel={
        typeof label === "string" ? `Other values for ${label}` : "Other values for this field"
      }
    />
  );
});
